type PromptFieldMetadataSource = {
  tag?: unknown;
  metadata?: unknown;
  options?: unknown;
  aiCompanionEnabled?: unknown;
  aiCompanionType?: unknown;
  aiCompanionUrl?: unknown;
  aiCompanionId?: unknown;
  aiCompanionName?: unknown;
  aiCompanionSystemPrompt?: unknown;
  aiCompanionShowInstructions?: unknown;
  aiCompanionHtml?: unknown;
};

type MetadataRecord = Record<string, unknown>;

const AI_COMPANION_DEFAULTS: MetadataRecord = {
  aiCompanionEnabled: false,
  aiCompanionType: null,
  aiCompanionUrl: null,
  aiCompanionId: null,
  aiCompanionName: null,
  aiCompanionSystemPrompt: null,
  aiCompanionShowInstructions: false,
  aiCompanionHtml: null,
};

const AI_COMPANION_KEYS = Object.keys(AI_COMPANION_DEFAULTS);

const isRecord = (value: unknown): value is MetadataRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasOwn = (record: MetadataRecord, key: string) =>
  Object.prototype.hasOwnProperty.call(record, key);

const hasMeaningfulPlacementValue = (value: unknown) =>
  value !== undefined && value !== null && value !== '';

const isDefaultishCompanionValue = (value: unknown) =>
  value === undefined || value === null || value === false || value === '';

export function buildPromptFieldMetadata(
  field: PromptFieldMetadataSource,
  existingMetadata?: unknown,
): MetadataRecord {
  const existing = isRecord(existingMetadata) ? existingMetadata : {};
  const incoming = isRecord(field.metadata) ? field.metadata : {};
  const merged: MetadataRecord = { ...existing, ...incoming };

  if (field.options) {
    merged.options = field.options;
  }

  for (const key of ['slot', 'tabId']) {
    if (hasMeaningfulPlacementValue(incoming[key])) {
      merged[key] = incoming[key];
    } else if (hasMeaningfulPlacementValue(existing[key])) {
      merged[key] = existing[key];
    } else if (!hasMeaningfulPlacementValue(merged[key])) {
      delete merged[key];
    }
  }

  for (const key of AI_COMPANION_KEYS) {
    if (hasOwn(incoming, key)) {
      merged[key] = incoming[key] ?? AI_COMPANION_DEFAULTS[key];
      continue;
    }

    if (hasOwn(field as MetadataRecord, key)) {
      const value = (field as MetadataRecord)[key];
      const existingValue = existing[key];

      if (
        isDefaultishCompanionValue(value) &&
        !isDefaultishCompanionValue(existingValue)
      ) {
        merged[key] = existingValue;
      } else {
        merged[key] = value ?? AI_COMPANION_DEFAULTS[key];
      }
      continue;
    }

    if (!hasOwn(merged, key)) {
      merged[key] = AI_COMPANION_DEFAULTS[key];
    }
  }

  return merged;
}
